import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SocketService } from './socket.service';
import { TokenService } from './token.service';
import { environment } from '../../../environments/environment';

export type EmergencyType =
    | 'llanta_pinchada'
    | 'sin_gasolina'
    | 'fallo_mecanico'
    | 'emergencia_medica'
    | 'incidente_pasajeros'
    | 'otro';

export interface TripEmergencyPayload {
    emergencyId: number;
    tripId: number;
    driverId: number;
    driverName: string;
    type: EmergencyType;
    lat: number;
    lng: number;
    createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class EmergencyService {
    private readonly http = inject(HttpClient);
    private readonly socket = inject(SocketService);
    private readonly tokens = inject(TokenService);

    private roomRejoinInitialized = false;

    report(tripId: number, type: EmergencyType, lat: number, lng: number): Observable<any> {
        return this.http.post(`${environment.apiUrl}/emergencies`, { tripId, type, lat, lng });
    }

    onEmergencyReceived(callback: (payload: TripEmergencyPayload) => void): void {
        this.socket.on('trip_emergency', callback);
    }

    offEmergencyReceived(callback: (payload: TripEmergencyPayload) => void): void {
        this.socket.off('trip_emergency', callback);
    }

    // Re-join the driver and tenant rooms on every (re)connect. The server
    // clears Socket.IO room memberships on disconnect, so this must fire after
    // each reconnect to keep emergency broadcasts reachable.
    //
    // Called once at app boot (app.component) so the handler lives for the whole
    // session, not just while the active-trip screen is mounted. The driverId is
    // read at connect time rather than at registration time, so a driver who logs
    // in after boot is joined on the reconnect that authenticate() triggers.
    initRoomRejoin(): void {
        if (this.roomRejoinInitialized) return;
        this.roomRejoinInitialized = true;

        this.socket.on('connect', () => this.joinRooms());
        this.joinRooms();
    }

    private joinRooms(): void {
        const driverId = this.getDriverIdFromToken();
        if (!driverId) return;
        this.socket.emit('join-driver', driverId);
        console.log(`[Emergency] (Re)joined driver-${driverId} and tenant emergency room.`);
    }

    private getDriverIdFromToken(): number | null {
        try {
            const token = this.tokens.getToken();
            if (!token) return null;
            const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
            const payload = JSON.parse(atob(b64));
            return payload.driverId ?? null;
        } catch {
            return null;
        }
    }
}
